import isEmpty from "lodash/isEmpty";
import stripeConstants from "./stripeConstants";
import { get } from "lodash";
import { isAllowAllFeatureByDev } from "../utils/devAccess";

const { STRIPE_ROLE } = stripeConstants;

const normalizeEmail = (email) => String(email || "").trim().toLowerCase();

export const isDownPdfDocxStarterMonthlyPlanDisable = (
  premiumStatusRole,
  subscriptionId
) => {
  return (
    [
      STRIPE_ROLE.STARTER_MONTHLY,
      STRIPE_ROLE.PRO_MONTHLY,
      STRIPE_ROLE.ENTERPRISE_MONTHLY,
      STRIPE_ROLE.STARTER_YEARLY,
      STRIPE_ROLE.PRO_YEARLY,
      STRIPE_ROLE.ENTERPRISE_YEARLY,
      STRIPE_ROLE.PDF_DOCX_STARTER_YEARLY,
    ].includes(premiumStatusRole) && !isEmpty(subscriptionId)
  );
};

export const isDownPdfDocxStarterYearlyPlanDisable = (
  premiumStatusRole,
  subscriptionId
) => {
  return (
    [
      STRIPE_ROLE.STARTER_YEARLY,
      STRIPE_ROLE.PRO_YEARLY,
      STRIPE_ROLE.ENTERPRISE_YEARLY,
    ].includes(premiumStatusRole) && !isEmpty(subscriptionId)
  );
};

export const isStarterMonthlyPlanDisable = (
  premiumStatusRole,
  subscriptionId
) => {
  return (
    [
      STRIPE_ROLE.PRO_MONTHLY,
      STRIPE_ROLE.ENTERPRISE_MONTHLY,
      STRIPE_ROLE.STARTER_YEARLY,
      STRIPE_ROLE.PRO_YEARLY,
      STRIPE_ROLE.ENTERPRISE_YEARLY,
    ].includes(premiumStatusRole) && !isEmpty(subscriptionId)
  );
};

export const isProMonthlyPlanDisable = (premiumStatusRole, subscriptionId) => {
  return (
    [
      STRIPE_ROLE.ENTERPRISE_MONTHLY,
      STRIPE_ROLE.STARTER_YEARLY,
      STRIPE_ROLE.PRO_YEARLY,
      STRIPE_ROLE.ENTERPRISE_YEARLY,
    ].includes(premiumStatusRole) && !isEmpty(subscriptionId)
  );
};

export const isEnterpriseMonthlyPlanDisable = (
  premiumStatusRole,
  subscriptionId
) => {
  return (
    [
      STRIPE_ROLE.STARTER_YEARLY,
      STRIPE_ROLE.PRO_YEARLY,
      STRIPE_ROLE.ENTERPRISE_YEARLY,
    ].includes(premiumStatusRole) && !isEmpty(subscriptionId)
  );
};

export const isStarterYearlyPlanDisable = (
  premiumStatusRole,
  subscriptionId
) => {
  return (
    [STRIPE_ROLE.PRO_YEARLY, STRIPE_ROLE.ENTERPRISE_YEARLY].includes(
      premiumStatusRole
    ) && !isEmpty(subscriptionId)
  );
};

export const isProYearlyPlanDisable = (premiumStatusRole, subscriptionId) => {
  return (
    [STRIPE_ROLE.ENTERPRISE_YEARLY].includes(premiumStatusRole) &&
    !isEmpty(subscriptionId)
  );
};

export const isEnterpriseYearlyPlanDisable = () => {
  return false;
};

export const isHireCoachPlanDisable = (premiumStatusRole, subscriptionId) => {
  return (
    STRIPE_ROLE.HIRE_COACH_WRITER === premiumStatusRole &&
    !isEmpty(subscriptionId)
  );
};

// Razorpay-written subscription documents (users-razorpay/{uid}/subscriptions,
// written by fulfillRazorpayPayment and razorpayWebhook in functions/index.js)
// carry only { status, role, provider, updatedAt }. There is no billing period
// on them, so their activeness comes from status alone.
const isRazorpaySubscriptionActive = (data = {}) =>
  data.provider === "razorpay" && data.status === "active";

// The Stripe extension writes current_period_end as a Firestore Timestamp, so
// the period check reads its `seconds`. A document without one cannot be
// period-checked and is not treated as active by this branch.
const isStripeSubscriptionActive = (data = {}, currentDateInSec) => {
  const periodEndInSec = get(data, "current_period_end.seconds");

  return (
    typeof periodEndInSec === "number" && currentDateInSec <= periodEndInSec
  );
};

// check is there any plan active for individual user resume
export const getIsAnySubsActiveByPlanOptions = (
  allSubscriptions = [],
  planOptions = []
) => {
  let isActiveCount = 0;

  if (!isEmpty(allSubscriptions)) {
    const currentDateInSec = Date.parse(new Date()) / 1000;

    allSubscriptions.forEach((item) => {
      const data = item?.data || {};

      const isActive =
        isRazorpaySubscriptionActive(data) ||
        isStripeSubscriptionActive(data, currentDateInSec);

      if (!isActive) return;

      planOptions.forEach((plan) => {
        if (data.role === plan) {
          isActiveCount = isActiveCount + 1;
        }
      });
    });
  }

  return isActiveCount ? true : false;
};

// The single gate for downloads. AI metering is resolveApiQuota / useAiQuota.
//
// An ACTIVE SUBSCRIPTION is now the only thing that unlocks either. The
// retired one-time "Unlimited Download" purchase deliberately grants nothing:
// its role is no longer in any planOptions list, so a legacy holder falls back
// to the free quota like anyone else.
//
// premiumStatusRole is intentionally NOT consulted here. It comes from a custom
// claim that is only refreshed when the ID token is, so it can outlive a
// cancelled or lapsed subscription by up to an hour; allSubscriptions is read
// live from Firestore and carries the billing period. allAccessUsers stays as
// the manual comp list (downloads only — AI is always metered on the server).
// REACT_APP_IS_ALLOW_ALL_FEATURE_BY_DEV unlocks downloads in local / QA builds.
export const hasUnlimitedResumeAccess = ({
  allSubscriptions = [],
  planOptions = [],
  email = "",
  allAccessUsers = [],
}) => {
  if (isAllowAllFeatureByDev()) return true;

  const needle = normalizeEmail(email);
  const isComp =
    !!needle &&
    (allAccessUsers || []).some(
      (entry) => normalizeEmail(entry) === needle
    );

  return (
    getIsAnySubsActiveByPlanOptions(allSubscriptions, planOptions) || isComp
  );
};
